/* global Office, window */

import { AuthDialogMessage } from "./AuthDialogMessage";
import { DialogCancelledError } from "./DialogCancelledError";

const DIALOG_EVENT_USER_CLOSED = 12006;

/**
 * Office Add-in Dialog API で認証用ウィンドウを開き、認証結果を受け取る。
 *
 * **必ずユーザー操作 (click 等) ハンドラ内で同期的に呼び出すこと。**
 * 非同期処理 (await / Promise.then) を挟むとブラウザのポップアップブロッカー
 * によって弾かれる可能性が高い。
 *
 * Office Dialog API は絶対 HTTPS URL を要求するため、相対 URL は
 * `window.location.origin` を基にした絶対 URL に正規化してから渡す。
 *
 * Dialog 内で発生し得る結果:
 * - 認証成功または失敗の messageParent 受信 → 該当ペイロードで resolve
 * - ユーザーが Dialog を閉じた → DialogCancelledError で reject
 * - その他のエラー → Error で reject
 */
export function openAuthDialog(url: string): Promise<AuthDialogMessage> {
    const absoluteUrl = toAbsoluteUrl(url);
    return new Promise((resolve, reject) => {
        Office.context.ui.displayDialogAsync(
            absoluteUrl,
            { height: 60, width: 30, displayInIframe: false },
            (result) => {
                if (result.status !== Office.AsyncResultStatus.Succeeded) {
                    reject(
                        new Error(`Failed to open dialog: ${result.error?.message ?? "unknown"}`)
                    );
                    return;
                }

                const dialog = result.value;

                dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
                    const messageStr = (arg as { message?: string }).message ?? "";
                    let parsed: AuthDialogMessage;
                    try {
                        parsed = JSON.parse(messageStr) as AuthDialogMessage;
                    } catch {
                        dialog.close();
                        reject(new Error("Invalid dialog message format"));
                        return;
                    }
                    dialog.close();
                    resolve(parsed);
                });

                dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: unknown) => {
                    const errorCode = (arg as { error?: number }).error;
                    if (errorCode === DIALOG_EVENT_USER_CLOSED) {
                        reject(new DialogCancelledError());
                    } else {
                        reject(new Error(`Dialog error: ${errorCode}`));
                    }
                });
            }
        );
    });
}

function toAbsoluteUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) return url;
    return new URL(url, window.location.origin).toString();
}
